import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Copy, Eye, QrCode, Download, 
  Settings, AlertTriangle 
} from 'lucide-react';
import { BusinessCard } from '../types';
import { githubApi } from '../services/githubApi';
import { authService } from '../services/authService';
import { cardServices } from '../services/cardServices';
import Header from '../components/Header';
import Loading from '../components/Loading';
import CardForm from '../components/admin/CardForm';

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<BusinessCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCard, setEditingCard] = useState<BusinessCard | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);

  useEffect(() => {
    // Check authentication
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Check session expiry
    if (authService.isSessionExpiringSoon()) {
      setSessionWarning(true);
    }

    loadCards();
  }, [navigate]);

  const loadCards = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await githubApi.fetchCards();
      
      if (result.success && result.data) {
        setCards(result.data.cards);
      } else {
        setError(result.error || 'Failed to load cards');
      }
    } catch (err) {
      setError('Failed to load cards');
      console.error('Error loading cards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCard = async (cardData: BusinessCard) => {
    setIsSaving(true);
    try {
      let result;
      
      if (editingCard) {
        result = await githubApi.updateCard(editingCard.slug, cardData);
      } else {
        result = await githubApi.createCard(cardData);
      }
      
      if (result.success) {
        setShowForm(false);
        setEditingCard(null);
        await loadCards(); // Reload cards
      } else {
        setError(result.error || 'Failed to save card');
      }
    } catch (err) {
      setError('Failed to save card');
      console.error('Error saving card:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteCard = async (card: BusinessCard) => {
    if (!window.confirm(`Are you sure you want to delete ${card.name}'s card?`)) {
      return;
    }

    try {
      const result = await githubApi.deleteCard(card.slug);
      
      if (result.success) {
        await loadCards(); // Reload cards
      } else {
        setError(result.error || 'Failed to delete card');
      }
    } catch (err) {
      setError('Failed to delete card');
      console.error('Error deleting card:', err);
    }
  };

  const handleCopyLink = async (card: BusinessCard) => {
    const cardUrl = `${window.location.origin}/card/${card.slug}`;
    try {
      await cardServices.copyToClipboard(cardUrl);
      // You could show a toast notification here
    } catch (err) {
      setError('Failed to copy link');
    }
  };

  const handleDownloadQR = async (card: BusinessCard) => {
    try {
      await cardServices.downloadQRCode(card);
    } catch (err) {
      setError('Failed to download QR code');
    }
  };

  const handleDownloadVCard = (card: BusinessCard) => {
    try {
      cardServices.downloadVCard(card);
    } catch (err) {
      setError('Failed to download vCard');
    }
  };

  const handlePreviewCard = (card: BusinessCard) => {
    window.open(`/card/${card.slug}`, '_blank');
  };

  const handleExtendSession = () => {
    authService.updateLastLogin();
    setSessionWarning(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header showAdminControls />
        <Loading fullScreen text="Loading admin panel..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header showAdminControls />
      
      {/* Session Warning */}
      {sessionWarning && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
              <p className="text-sm text-yellow-700">
                Your session will expire in {authService.getRemainingSessionTime()} minutes
              </p>
            </div>
            <button
              onClick={handleExtendSession}
              className="text-sm text-yellow-700 hover:text-yellow-900 underline"
            >
              Extend Session
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Manage your digital business cards
            </p>
          </div>
          
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add New Card</span>
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Cards Grid */}
        {cards.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <Settings className="h-12 w-12" />
            </div>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No cards yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first business card.
            </p>
            <div className="mt-6">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Card
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {cards.map((card) => (
              <div key={card.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Card Preview */}
                <div className="p-4 border-b">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {card.image && (
                        <img
                          src={card.image}
                          alt={card.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900">{card.name}</h3>
                        <p className="text-sm text-gray-600">{card.position}</p>
                      </div>
                    </div>
                    {card.isMainCard && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
                        Main
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>📧 {card.email}</p>
                    <p>📞 {card.phone}</p>
                    {card.website && <p>🌐 {card.website}</p>}
                  </div>
                </div>

                {/* Actions */}
                <div className="p-4 space-y-2">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePreviewCard(card)}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                      <span>Preview</span>
                    </button>
                    
                    <button
                      onClick={() => handleCopyLink(card)}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                      title="Copy Link"
                    >
                      <Copy className="h-4 w-4" />
                      <span>Copy</span>
                    </button>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleDownloadQR(card)}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                      title="Download QR"
                    >
                      <QrCode className="h-4 w-4" />
                      <span>QR</span>
                    </button>
                    
                    <button
                      onClick={() => handleDownloadVCard(card)}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                      title="Download vCard"
                    >
                      <Download className="h-4 w-4" />
                      <span>vCard</span>
                    </button>
                  </div>
                  
                  <div className="flex space-x-2 pt-2 border-t">
                    <button
                      onClick={() => {
                        setEditingCard(card);
                        setShowForm(true);
                      }}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                      <span>Edit</span>
                    </button>
                    
                    <button
                      onClick={() => handleDeleteCard(card)}
                      className="flex-1 flex items-center justify-center space-x-1 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Card Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingCard ? 'Edit Card' : 'Add New Card'}
                </h2>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingCard(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <CardForm
                card={editingCard || undefined}
                onSave={handleSaveCard}
                onCancel={() => {
                  setShowForm(false);
                  setEditingCard(null);
                }}
                isLoading={isSaving}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin; 